import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';
import axios from '../config/axiosConfig';
import Toast from './Toast';

const ResetPasswordWithToken = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [formData, setFormData] = useState({
        newPassword: '',
        confirmPassword: ''
    });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    
    const token = searchParams.get('token');
    const email = searchParams.get('email');

    useEffect(() => {
        // Redirect if token or email is missing
        if (!token || !email) {
            setError('Invalid or missing reset link. Please request a new password reset.');
            setTimeout(() => navigate('/forgot-password'), 3000);
        }
    }, [token, email, navigate]);

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 5000);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        // Validation
        if (formData.newPassword.length < 6) {
            setError('Password must be at least 6 characters long');
            return;
        }

        if (formData.newPassword !== formData.confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (!token || !email) {
            setError('Invalid reset link. Please request a new password reset.');
            return;
        }

        setIsLoading(true);

        try {
            await axios.post(API_ENDPOINTS.AUTH_RESET_PASSWORD_TOKEN, {
                token,
                email,
                newPassword: formData.newPassword
            });

            setSuccess(true);
            showToast('Password reset successfully! Redirecting to login...', 'success');

            // Redirect to login after 2 seconds
            setTimeout(() => {
                navigate('/login');
            }, 2000);
        } catch (error) {
            console.error('Password reset error:', error);
            const errorMessage = error.response?.data?.message || 'Failed to reset password. The link may have expired.';
            setError(errorMessage);
            showToast(errorMessage, 'error');
        } finally {
            setIsLoading(false);
        }
    };

    if (success) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center px-4">
                <div className="max-w-md w-full">
                    <div className="text-center mb-8">
                        <img src="/logo-bright.svg" alt="RestroAI" className="h-16 w-auto mx-auto mb-6" />
                    </div>
                    <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-8 border border-white/20 shadow-2xl">
                        <div className="bg-green-500/20 border border-green-500/30 rounded-xl p-6 text-center">
                            <div className="text-4xl mb-4">✅</div>
                            <h3 className="text-xl font-bold text-green-200 mb-2">Password Reset Successful!</h3>
                            <p className="text-green-200/80 text-sm">
                                Your password has been reset successfully. Redirecting to login...
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center px-4">
            <div className="max-w-md w-full">
                {/* Logo and Header */}
                <div className="text-center mb-8">
                    <img src="/logo-bright.svg" alt="RestroAI" className="h-16 w-auto mx-auto mb-6" />
                    <h2 className="text-3xl font-bold text-white mb-2">Reset Your Password</h2>
                    <p className="text-white/70">Enter your new password below</p>
                </div>

                {/* Password Reset Form */}
                <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-8 border border-white/20 shadow-2xl">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div>
                            <label htmlFor="newPassword" className="block text-sm font-semibold text-white mb-2">
                                New Password
                            </label>
                            <input
                                id="newPassword"
                                type="password"
                                name="newPassword"
                                value={formData.newPassword}
                                onChange={handleInputChange}
                                placeholder="Enter your new password (min. 6 characters)"
                                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                                required
                                minLength={6}
                                disabled={isLoading || !token || !email}
                            />
                        </div>

                        <div>
                            <label htmlFor="confirmPassword" className="block text-sm font-semibold text-white mb-2">
                                Confirm New Password
                            </label>
                            <input
                                id="confirmPassword"
                                type="password"
                                name="confirmPassword"
                                value={formData.confirmPassword}
                                onChange={handleInputChange}
                                placeholder="Confirm your new password"
                                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                                required
                                minLength={6}
                                disabled={isLoading || !token || !email}
                            />
                        </div>

                        {/* Error Message */}
                        {error && (
                            <div className="bg-red-500/20 border border-red-500/30 rounded-xl p-4">
                                <div className="flex items-center space-x-2">
                                    <span className="text-red-400">⚠️</span>
                                    <span className="text-red-200 text-sm">{error}</span>
                                </div>
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={isLoading || !token || !email}
                            className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white py-3 px-6 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-lg"
                        >
                            {isLoading ? (
                                <div className="flex items-center justify-center">
                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                    Resetting password...
                                </div>
                            ) : (
                                'Reset Password'
                            )}
                        </button>
                    </form>
                </div>

                {/* Links */}
                <div className="text-center mt-6 space-y-2">
                    <Link to="/forgot-password" className="block text-white/70 hover:text-white/90 text-sm transition-colors">
                        Request a new reset link
                    </Link>
                    <Link to="/login" className="block text-white/70 hover:text-white/90 text-sm transition-colors">
                        ← Back to Login
                    </Link>
                </div>
            </div>

            {/* Toast Notification */}
            {toast.show && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast({ show: false, message: '', type: 'success' })}
                />
            )}
        </div>
    );
};

export default ResetPasswordWithToken;
